import { defineNuxtRouteMiddleware, showError, useError } from "nuxt/app";
import { isClient, useBugReporter, usePinia } from "#imports";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useCustomerStore } from "~/stores/customer";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";

const abortWithUnexpectedError = (
  message: string = "We could not load the loyalty settings. Please try again in a few minutes.",
) => {
  throw showError({
    statusCode: 500,
    statusMessage: message,
  });
};

export default defineNuxtRouteMiddleware(() => {
  if (useError().value || !isClient()) return;

  const pinia = usePinia();
  const business = useBusinessStore(pinia);
  const outlet = useOutletStore(pinia);
  const loyaltyProgram = useLoyaltyProgramStore(pinia);
  const customer = useCustomerStore(pinia);

  if (!business.isLoaded) {
    return abortWithUnexpectedError(
      "BootstrapMiddleware must run after outlet has been loaded only.",
    );
  }

  if (!outlet.isLoaded) {
    return abortWithUnexpectedError(
      "EnsureOutletIsLoadedMiddleware must run after outlet has been loaded only.",
    );
  }

  if (
    loyaltyProgram.isLoaded &&
    loyaltyProgram.outletId === outlet.id &&
    business.salesChannel === loyaltyProgram.salesChannel
  ) {
    return;
  }

  try {
    Promise.all([
      loyaltyProgram.load({
        businessId: business.id,
        outletId: outlet.id,
        salesChannel: business.salesChannel,
      }),
      customer.syncLoyalty(business.id, business.salesChannel, outlet.id),
    ]);
  } catch (error: unknown) {
    useBugReporter().report(error, {
      tags: {
        "code.flag": "middleware-loyalty-load",
      },
    });
  }
});
